import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import { isSuccess } from 'utils/responseStatus';

import { useLoginMutation } from 'bus/auth/services';
import { Login } from 'bus/auth/types';

import { setUser as setUserAction } from '../bus/auth/actions';
import { ApiLogin } from '../services';

export type IAuth = {
  isAuth?: String;
  loading?: boolean;
  isAdmin?: boolean;
  user?: Login;
  claims?: Array<string>;
  signOut?: () => void;
  signIn?: (userName: string, password: string) => void;
  children?: React.ReactNode;
  getClaims?: (claimsBlock: Array<string>) => void;
};

const AuthProvider: React.FC<IAuth> = (props) => {
  const dispatch = useDispatch();
  const [login] = useLoginMutation();

  const [isAuth, setIsAuth] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState<any>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [claims, setClaims] = useState<Array<string>>(['all']);
  const [user, setUser] = useState<Login | null>(null);
  const location = useLocation();
  const history = useHistory();

  const initialization = async () => {
    try {
      const userInfo = localStorage.getItem('userInfo');

      if (userInfo) {
        const userInfoJSON: Login = JSON.parse(userInfo);

        if (userInfoJSON) {
          if (userInfoJSON.userId <= 1) {
            setIsAdmin(true);
          }
          if (userInfoJSON.expires > Date.now() / 1000) {
            const result = await ApiLogin.claims();
            setUser(userInfoJSON);
            setClaims(result.data);
            setIsAuth(true);
            history.push(window.location.pathname + window.location.search);
            dispatch(setUserAction(userInfoJSON.fullName));
          } else {
            localStorage.removeItem('userInfo');
            setIsAuth(null);
          }
        }
      }
    } catch (e) {
      console.log('e', e);
    }
    setLoading(false);
  };

  const signOut = async () => {
    try {
      await ApiLogin.logOut();
    } finally {
      localStorage.removeItem('userInfo');
      setIsAuth(false);
      setUser(null);
      setClaims(['all']);
      window.history.replaceState(null, '', ' ');
    }
  };

  useEffect(() => {
    initialization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.hash === '#logout-auth') {
      signOut();
    }
  }, [location]);

  const getClaims = (claimsBlock: Array<string>) => {
    if (claimsBlock!.indexOf('all') > -1) {
      return true;
    }

    let guardBoard = false;

    for (let item of claimsBlock) {
      if (claims!.indexOf(item) >= 0) {
        guardBoard = true;
        break;
      } else {
        guardBoard = false;
      }
    }
    return guardBoard;
  };

  const defaultPage = (defaultUrl: string) => {
    if (defaultUrl) {
      history.push(`${defaultUrl}`);
    } else {
      history.replace('/task');
    }
  };

  const signIn = async (userName: string, password: string) => {
    login({ userName, password }).then((response) => {
      if (isSuccess(response)) {
        //@ts-ignore
        const data: Login = response.data;

        localStorage.setItem('userInfo', JSON.stringify(data));
        setClaims(data.claims);
        setUser(data);
        if (data.expires > Date.now() / 1000) {
          dispatch(setUserAction(data.fullName));
        }
        setIsAuth(true);
        setTimeout(() => {
          defaultPage(data.defaultPage);
        }, 200);
      }
    });
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <AuthContext.Provider value={{ isAuth, isAdmin, loading, signOut, signIn, claims, getClaims, user }}>
      {props.children}
    </AuthContext.Provider>
  );
};

const AuthContext = createContext({});
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
